/* oxlint-disable typescript/no-non-null-assertion -- outsmart compiler */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import {
  createPath,
  createSearchParams,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router';
import type { FlowStep, Params, PathSpec } from './flow-steps';
import { FlowGraph } from './flow-graph';
import { buildGraph } from './build-graph';
import { findStep } from './find-step';

export interface Flow<State> {
  id: string;
  params?: Params;
  goBack?: (params?: Params) => void;
  goNext: (options?: { params?: Params; state?: State }) => void;
  goStep: (id: string, params?: Params) => void;
  complete: () => void;
  close: () => void;
}

const context = createContext<Flow<unknown> | null>(null);

export const flowContext = context;

export function useFlow<State = unknown>(): Flow<State> {
  const ctx = useContext(context);
  if (ctx === null) {
    throw new Error('⚠️ Not in a flow');
  }
  return ctx;
}

/**
 * Returns `candidate` only when it is a same-origin path (e.g. `/mandates`), otherwise
 * `fallback`. Rejects protocol-relative (`//evil.com`), backslash-prefixed (`/\evil.com`),
 * absolute URLs (`http://…`) and other schemes (`javascript:…`) to prevent open-redirects.
 */
export function parseFromParam(candidate: string | null, fallback: string): string {
  if (candidate && /^\/[^/\\]/.test(candidate)) {
    return candidate;
  }
  return fallback;
}

export function mergeParams(existing: URLSearchParams, newParams: Params = {}): URLSearchParams {
  const merged = createSearchParams(existing.toString());

  for (const [key, val] of Object.entries(newParams)) {
    if (val === undefined) {
      merged.delete(key);
      continue;
    }
    if (Array.isArray(val)) {
      merged.delete(key);
      for (const v of val) {
        merged.append(key, v);
      }
    } else {
      merged.set(key, val);
    }
  }

  return merged;
}

function buildPath(path: string, params: Params = {}, existingParams?: URLSearchParams): string {
  const search = mergeParams(existingParams ?? new URLSearchParams(), params).toString();
  return createPath({
    pathname: path,
    search,
  });
}

export interface FlowOnCompleteOptions<StateData = undefined> {
  from?: string;
  state?: StateData;
}

export type FlowOnComplete<StateData = undefined> = (
  options: FlowOnCompleteOptions<StateData>
) => void;

export interface FlowOnCloseOptions<StateData = undefined> {
  from?: string;
  currentStep: PathSpec | null;
  state?: StateData;
}

export type FlowOnClose<StateData = undefined> = (options: FlowOnCloseOptions<StateData>) => void;

export interface FlowProviderProps<State> {
  children: React.ReactNode;
  steps: Record<string, FlowStep<State>>;
  basePath: string;
  entryStep: PathSpec;
  state: State;
  /**
   * Default URL to navigate to when closing the flow. Can be overridden by "from" search param.
   */
  fromFallback?: string;
  /**
   * Callback called when flow is completed by calling complete() on the last step.
   */
  onComplete?: FlowOnComplete;
  /**
   * Callback called when flow is closed and by pressing ESC key.
   */
  onClose?: FlowOnClose;
  /**
   * When true, close() on last step calls complete() instead (default: true)
   */
  completeOnCloseLastStep?: boolean;
  /**
   * When true, the flow header back control is hidden on the first in-flow URL if that URL is
   * not the entry step (e.g. deep link). After navigating forward, normal graph back applies.
   */
  hideBackButtonWhenStartingFromCustomStep?: boolean;
}

export function FlowProvider<State>({
  children,
  steps,
  basePath,
  entryStep,
  state,
  fromFallback = '/',
  onComplete,
  onClose,
  completeOnCloseLastStep = true,
  hideBackButtonWhenStartingFromCustomStep = false,
}: FlowProviderProps<State>): React.JSX.Element | null {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const firstLandingPathnameRef = useRef<string | null>(null);
  const from = useMemo(
    () => parseFromParam(searchParams.get('from'), fromFallback),
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- only needs to run on mount
    []
  );
  const graph = new FlowGraph();

  buildGraph({ graph, spec: entryStep, steps, state, basePath });

  const currentNode = graph.getNode(pathname);

  // oxlint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
  const entryPath = basePath + findStep({ id: entryStep.id, steps }).path(entryStep.params ?? {});
  const entryNode = graph.getNode(entryPath);
  const lastNode = entryNode === undefined ? null : graph.lastNode(entryNode);

  if (currentNode !== undefined && firstLandingPathnameRef.current === null) {
    firstLandingPathnameRef.current = pathname;
  }

  const suppressBack =
    hideBackButtonWhenStartingFromCustomStep &&
    pathname !== entryPath &&
    pathname === firstLandingPathnameRef.current;

  useEffect(() => {
    // Navigate to the last eligible step when user strays from the path
    if (currentNode === undefined && lastNode !== null) {
      const path = buildPath(lastNode.path, {}, searchParams);
      void navigate(path, { replace: true });
    }
  }, [currentNode, lastNode, navigate, pathname, searchParams]);

  const complete = useCallback((): void => {
    if (onComplete) {
      onComplete({ from });
    } else {
      void navigate(from);
    }
  }, [from, navigate, onComplete]);

  const closeFlow = useCallback((): void => {
    const currentStepHasNoNext = currentNode ? steps[currentNode.id]?.next === undefined : false;

    if (completeOnCloseLastStep && currentStepHasNoNext) {
      complete();
      return;
    }

    if (onClose) {
      onClose({
        from,
        currentStep: currentNode ? { id: currentNode.id, params: currentNode.params } : null,
      });
      return;
    }

    void navigate(from);
  }, [currentNode, steps, from, navigate, onClose, complete, completeOnCloseLastStep]);

  // Handle ESC key to close flow or blur focused elements
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;

      // If a component already handled ESC (e.g., closing a dropdown), respect that
      if (event.defaultPrevented) return;

      // If something has focus, blur it first
      if (
        (document.activeElement instanceof HTMLElement ||
          document.activeElement instanceof SVGElement) &&
        document.activeElement !== document.body
      ) {
        document.activeElement.blur();
      } else {
        // Nothing focused, close the flow
        closeFlow();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeFlow]);

  const goBack = useCallback(
    (params?: Params): void => {
      if (suppressBack) return;
      const node = currentNode!;
      const target = node.back ?? node.prev;
      if (target !== null) {
        void navigate(buildPath(target.path, params, searchParams));
      }
    },
    [currentNode, navigate, searchParams, suppressBack]
  );

  const goNext = useCallback(
    (options: { params?: Params; state?: State } = {}): void => {
      const stateToUse = options.state ?? state;
      const newGraph = new FlowGraph();
      buildGraph({
        graph: newGraph,
        spec: entryStep,
        steps,
        state: stateToUse,
        basePath,
      });
      const node = newGraph.getNode(pathname);

      if (!node) {
        throw new Error(`Current pathname "${pathname}" not found in graph`);
      }

      if (node.next !== null) {
        const path = buildPath(node.next.path, options.params, searchParams);
        void navigate(path);
      }
    },
    [basePath, entryStep, navigate, pathname, searchParams, state, steps]
  );

  const goStep = useCallback(
    (id: string, params: Params = {}): void => {
      const step = findStep({ id, steps });
      const basePlusPath = basePath + step.path(params);
      const path = buildPath(basePlusPath, params, searchParams);

      void navigate(path);
    },
    [basePath, navigate, searchParams, steps]
  );

  const contextValue = useMemo(
    () =>
      // oxlint-disable-next-line typescript/consistent-type-assertions
      ({
        id: currentNode?.id,
        params: currentNode?.params,
        goNext,
        goBack: currentNode?.prev === null || suppressBack ? undefined : goBack,
        goStep,
        complete,
        close: closeFlow,
      }) as Flow<unknown>,
    [currentNode, goNext, goBack, goStep, complete, closeFlow, suppressBack]
  );

  if (currentNode === undefined) {
    return null;
  }

  return <context.Provider value={contextValue}>{children}</context.Provider>;
}
